const AnimationLayout = {
    Horizontal: 0,
    Vertical: 1,
};

export type Animation = {
    name: string;
    row: number;
    col: number;
    frames: number;
};

export type AnimationCollection = {
    [key: string]: Animation;
};

export type Frame = {
    row: number;
    col: number;
};

export class Animator {
    public currentAnimation: Animation | null = null;
    public currentFrameIndex: number = 0;

    public elapsedTime: number = 0;

    /**
     * Frame Duration is in milliseconds
     */
    private frameDuration: number = 0.25;
    public frameDirection: number = AnimationLayout.Horizontal;

    private _animations: AnimationCollection = {};

    /**
     * Determines whether or not the animation is looped.
     */
    public loop: boolean = true;
    public playing: boolean = false;

    constructor(animations: Animation[] | null = null) {
        // If given a set of animations then update the list
        if (animations)
            animations.forEach((anim) => {
                this.addAnimation(anim);
            });
    }

    static fromCollection(collection: AnimationCollection): Animator {
        var result = new Animator();
        result._animations = collection;
        return result;
    }

    addAnimation(anim: Animation) {
        this._animations[anim.name] = anim;
    }

    addAnimations(anims: Animation[]) {
        anims.forEach((a) => this.addAnimation(a));
    }

    play(name: string) {
        if (this.currentAnimation?.name !== name && name in this._animations) {
            this.currentAnimation = this._animations[name];
            this.currentFrameIndex = 0;
            this.elapsedTime = 0;
        }
        this.playing = true;
    }

    update = (delta: number): void => {
        // If not playing, stop here, nothing to update.
        if (!this.playing) return;

        // If there is no animation selected there
        // is currently nothing to update.
        if (!this.currentAnimation) return;

        // Animations with 0 frames should never be considered
        if (this.currentAnimation.frames <= 0) return;

        // Update the elapsed time.
        this.elapsedTime += delta;

        // If enough time has passed
        if (this.elapsedTime >= this.frameDuration) {
            // Reset the elapsed time
            this.elapsedTime = 0;

            // We use nextFrameIndex here so we can use modulus
            // operator on the frame index and still check if
            // the animation has completed
            const nextFrameIndex = this.currentFrameIndex + 1;

            // Check if the animation is finished.
            const finished = nextFrameIndex == this.currentAnimation.frames;

            // If we have reached the last frame, we can call the completion callback
            if (finished) this.onAnimationComplete();

            // If the animation is not finished or it isbeing looped, assign the
            // next frame index to the current frame index for the next visual.
            // If the animation is finished and we're not looping, this will ensure
            // the currentFrameIndex stays the last frame.
            if (!finished || this.loop)
                this.currentFrameIndex =
                    nextFrameIndex % this.currentAnimation.frames;
            else this.playing = false; // stop playing
        }
    };

    getAnimation(name: string): Animation {
        return this._animations[name] || null;
    }

    onAnimationComplete = () => {};

    setSpeedMs(ms: number): void {
        this.frameDuration = ms / 1000;
    }

    setSpeed(sec: number): void {
        this.frameDuration = sec;
    }

    getCurrentFrame(): Frame {
        if (!this.currentAnimation) {
            return {
                row: 0,
                col: 0,
            }; // No animation is currently playing
        }

        // If frames are laid out horizontally then we need to adjust the column index
        // based on the current frame index.
        if (this.frameDirection === AnimationLayout.Horizontal) {
            return {
                row: this.currentAnimation.row,
                col: this.currentAnimation.col + this.currentFrameIndex,
            };
        }

        // If frames are laid out vertically then we need to adjust the row index
        // based on the current frame index.
        return {
            row: this.currentAnimation.row + this.currentFrameIndex,
            col: this.currentAnimation.col,
        };
    }
}
